import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import {
  EventBusy as NoJobsIcon,
  AddTask as AvailableIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import MaidPortalLayout from '../components/maid-portal/MaidPortalLayout';
import MaidJobCard from '../components/maid-portal/MaidJobCard';
import MaidProfilePanel from '../components/maid-portal/MaidProfilePanel';
import AvailableJobsView from '../components/maid-portal/AvailableJobsView';
import MaidEarningsView from '../components/maid-portal/MaidEarningsView';
import PeriodFilter from '../components/PeriodFilter';
import { getPeriodRange } from '../utils/periodRange';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Kanban columns driven by job status
const BOARD_COLUMNS = [
  { id: 'NOT_STARTED', title: 'Upcoming', color: 'warning' },
  { id: 'IN_PROGRESS', title: 'In Progress', color: 'info' },
  { id: 'COMPLETED', title: 'Done', color: 'success' },
  { id: 'CANCELLED', title: 'Cancelled', color: 'error' }
];

const columnForStatus = (status) => {
  switch (status) {
    case 'IN_PROGRESS':
      return 'IN_PROGRESS';
    case 'COMPLETED':
      return 'COMPLETED';
    case 'CANCELLED':
      return 'CANCELLED';
    default:
      return 'NOT_STARTED';
  }
};

const MaidPortalPage = () => {
  const { user } = useAuth();
  const [view, setView] = useState('schedule');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maidId, setMaidId] = useState(null);

  const [periodType, setPeriodType] = useState('week'); // 'list' | 'week' | 'month'
  const isList = periodType === 'list';
  const [anchor, setAnchor] = useState(new Date());

  // Resolve the maid's userId — JobAssignment.maidId = User.id
  useEffect(() => {
    if (user?.id) {
      setMaidId(user.id);
    }
  }, [user]);

  // List mode has no date range: it loads every job
  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => (periodType === 'list' ? { start: null, end: null } : getPeriodRange(periodType, anchor)),
    [periodType, anchor]
  );

  const fetchJobs = useCallback(async () => {
    if (!maidId) return;
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_URL}/api/jobs/maid/${maidId}`, {
        params: rangeStart && rangeEnd
          ? { scheduledStartMin: rangeStart.toISOString(), scheduledStartMax: rangeEnd.toISOString() }
          : {},
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = response.data;
      setJobs(Array.isArray(data) ? data : data.jobs || []);
    } catch (err) {
      logger.log('Error fetching maid jobs:', err);
      setError('Could not load your jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [maidId, rangeStart, rangeEnd]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const jobTime = (job) => new Date(job.scheduledStartTime || job.utcScheduledDate || 0).getTime();
  const sortByTime = (a, b) => jobTime(a) - jobTime(b);

  // List mode: today and later in date order, then past jobs most recent first
  const listSections = useMemo(() => {
    if (!isList) return [];
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const upcoming = jobs.filter((j) => jobTime(j) >= startOfToday.getTime()).sort((a, b) => jobTime(a) - jobTime(b));
    const past = jobs.filter((j) => jobTime(j) < startOfToday.getTime()).sort((a, b) => jobTime(b) - jobTime(a));
    return [
      { id: 'upcoming', title: 'Upcoming', color: 'warning', jobs: upcoming, empty: 'No upcoming jobs.' },
      { id: 'past', title: 'Past', color: 'default', jobs: past, empty: 'No past jobs yet.' },
    ];
  }, [isList, jobs]);

  return (
    <MaidPortalLayout activeView={view} onViewChange={setView}>
      {view === 'profile' && <MaidProfilePanel />}
      {view === 'earnings' && <MaidEarningsView />}
      {view === 'available' && (
        <AvailableJobsView onClaimed={() => { fetchJobs(); setView('schedule'); }} />
      )}
      {view === 'schedule' && (
        <>
          <PeriodFilter
            periodType={periodType}
            anchor={anchor}
            start={rangeStart}
            end={rangeEnd}
            onPeriodTypeChange={setPeriodType}
            onAnchorChange={setAnchor}
            showList
          />

          {loading && (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          {!loading && !error && jobs.length === 0 && (
            <Box textAlign="center" py={6}>
              <NoJobsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary" gutterBottom>
                {isList ? 'You have no jobs yet.' : `No jobs for this ${periodType}.`}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AvailableIcon />}
                onClick={() => setView('available')}
                sx={{ mt: 1 }}
              >
                Browse Available Jobs
              </Button>
            </Box>
          )}

          {!loading && !error && jobs.length > 0 && isList && (
            <Box sx={{ maxWidth: 720, mx: 'auto' }}>
              {listSections.map((section) => (
                <Box key={section.id} mb={3}>
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <Typography variant="subtitle1" fontWeight={600}>{section.title}</Typography>
                    <Chip label={section.jobs.length} size="small" color={section.color} />
                  </Box>
                  {section.jobs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>{section.empty}</Typography>
                  ) : (
                    section.jobs.map((job) => <MaidJobCard key={job.id} job={job} onRefresh={fetchJobs} />)
                  )}
                </Box>
              ))}
            </Box>
          )}

          {!loading && !error && jobs.length > 0 && !isList && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                pb: 1,
                alignItems: 'flex-start',
                // Snap one column at a time on phone-width screens
                scrollSnapType: { xs: 'x mandatory', md: 'none' }
              }}
            >
              {BOARD_COLUMNS.map((column) => {
                const columnJobs = jobs
                  .filter((job) => columnForStatus(job.status) === column.id)
                  .sort(sortByTime);
                return (
                  <Box
                    key={column.id}
                    sx={{
                      flex: '1 0 260px',
                      minWidth: 260,
                      bgcolor: 'grey.100',
                      borderRadius: 2,
                      p: 1.5,
                      scrollSnapAlign: { xs: 'start', md: 'none' }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {column.title}
                      </Typography>
                      <Chip label={columnJobs.length} size="small" color={column.color} />
                    </Box>

                    {columnJobs.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
                        No jobs.
                      </Typography>
                    ) : (
                      columnJobs.map((job) => (
                        <MaidJobCard key={job.id} job={job} onRefresh={fetchJobs} />
                      ))
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </>
      )}
    </MaidPortalLayout>
  );
};

export default MaidPortalPage;
